import { computed } from "../../core/signals/primitives.js";
import { defineBehavior } from "../../core/composition/define-behavior.js";
import { createMachineReactor } from "../../core/reactors/create-machine-reactor.js";
import { listen } from "@videojs/utils/dom";
//#region src/playback/behaviors/dom/track-load-triggers.ts
/**
* Slot-driven FSM. The slot `loadActivated` is the canonical externally- observable state; `deriveState` reads it (and
* the preconditions) to derive the local FSM state. The slot is _both_ the state and the data — no separate internal
* bookkeeping.
*
*     'preconditions-unmet' ⟷ 'monitoring' ⟷ 'load-active'
*
*     preconditions-unmet → monitoring        element + URL appear
*     monitoring          → load-active       slot flips true (listener fires
*                                             or external write)
*     load-active         → monitoring        within-state cleanup resets slot
*                                             (URL or element identity change)
*     any                 → preconditions-unmet  element or URL → undefined
*
*     any state → destroying → destroyed       on destroy()
*/
function deriveState(presentation, mediaElement, loadActivated) {
	if (!mediaElement || !presentation?.url) return "preconditions-unmet";
	if (loadActivated) return "load-active";
	return "monitoring";
}
/**
* Track preload-overriding events per source.
*
* Writes `state.loadActivated = true` the first time a `play` or `seeking` event fires on the attached media element
* for the current source — or immediately on entry if the element is already committed to loading (`el.autoplay`,
* `!el.paused`, or `el.seeking`), covering autoplay, native-controls, and direct-DOM-`play()` scenarios.
*
* Sticky-true _within a source identity_: subsequent play/pause/seek cycles don't flip back. Source identity =
* (mediaElement, presentation URL). Either changing — including direct in-place swap with no `undefined` intermediate —
* resets the slot to `false`.
*
* Multi-writer with `hls/adapter.ts:play()` (which writes `true` directly on programmatic play) is intentional —
* different domains. The adapter records programmatic intent; this behavior is the DOM-side observer. Pre-existing
* `true` writes are honored because `deriveState` reads the slot — a `true` value routes directly to `'load-active'`
* without entering `'monitoring'`.
*
* @example
*   const reactor = trackLoadTriggers.setup({ state, context });
*   // later:
*   reactor.destroy();
*/
function trackLoadTriggersSetup({ state, context }) {
	const derivedStateSignal = computed(() => deriveState(state.presentation.get(), context.mediaElement.get(), state.loadActivated.get()));
	const urlSignal = computed(() => state.presentation.get()?.url);
	return createMachineReactor({
		initial: "preconditions-unmet",
		monitor: () => derivedStateSignal.get(),
		states: {
			"preconditions-unmet": {},
			monitoring: { effects: () => {
				const el = context.mediaElement.get();
				const setLoadActivated = () => state.loadActivated.set(true);
				if (el.autoplay || !el.paused || el.seeking) {
					setLoadActivated();
					return;
				}
				const cleanupPlay = listen(el, "play", setLoadActivated);
				const cleanupSeeking = listen(el, "seeking", setLoadActivated);
				return () => {
					cleanupPlay();
					cleanupSeeking();
				};
			} },
			"load-active": { effects: () => {
				context.mediaElement.get();
				urlSignal.get();
				return () => state.loadActivated.set(false);
			} }
		}
	});
}
const trackLoadTriggers = defineBehavior({
	stateKeys: ["loadActivated", "presentation"],
	contextKeys: ["mediaElement"],
	setup: trackLoadTriggersSetup
});
//#endregion
export { trackLoadTriggers };
